import { useMemo, useState, type ReactNode } from "react";

import { formatPrice } from "../../lib/tradingDisplay";
import { buildCommandCenterRowsV2 } from "../../lib/commandCenterV2/buildRows";
import type { CommandCenterOpenTradeLike, CommandCenterRowV2, CommandCenterSession, CommandCenterSignalLike, CommandCenterStrategyState } from "../../lib/commandCenterV2/types";
import type { IntelData } from "../../lib/translate";

interface CommandCenterV2Props {
  symbols: readonly string[];
  session: CommandCenterSession;
  intelMap: Record<string, IntelData | undefined>;
  signals: CommandCenterSignalLike[];
  openTrades: CommandCenterOpenTradeLike[];
  loading?: boolean;
  error?: Error;
  transportLabel: string;
}

const S = {
  shell: {
    display: "grid",
    gap: "12px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    padding: "12px 14px",
    borderRadius: "14px",
    border: "1px solid rgba(148,163,184,0.16)",
    background: "linear-gradient(135deg, rgba(15,23,42,0.96), rgba(12,12,24,0.88))",
  },
  title: {
    color: "#f8fafc",
    fontSize: "15px",
    fontWeight: 900,
    letterSpacing: "0.04em",
    textTransform: "uppercase" as const,
  },
  subtitle: {
    marginTop: "3px",
    color: "#94a3b8",
    fontSize: "11px",
  },
  health: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap" as const,
    justifyContent: "flex-end",
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    borderRadius: "999px",
    padding: "4px 8px",
    fontSize: "10px",
    fontWeight: 800,
    border: "1px solid rgba(255,255,255,0.08)",
  },
  grid: {
    overflowX: "auto" as const,
    borderRadius: "14px",
    border: "1px solid rgba(148,163,184,0.14)",
    background: "rgba(12,12,24,0.88)",
  },
  table: {
    width: "100%",
    minWidth: "1560px",
    borderCollapse: "collapse" as const,
    fontSize: "11px",
  },
  th: {
    textAlign: "left" as const,
    padding: "9px 10px",
    color: "#94a3b8",
    fontSize: "9px",
    fontWeight: 900,
    letterSpacing: "0.1em",
    textTransform: "uppercase" as const,
    borderBottom: "1px solid rgba(148,163,184,0.14)",
  },
  td: {
    padding: "10px",
    color: "#dbeafe",
    borderBottom: "1px solid rgba(148,163,184,0.08)",
    verticalAlign: "top" as const,
  },
  sym: {
    color: "#f8fafc",
    fontSize: "15px",
    fontWeight: 950,
  },
  muted: {
    color: "#64748b",
    fontSize: "10px",
  },
  strong: {
    color: "#e2e8f0",
    fontWeight: 850,
  },
  detail: {
    borderRadius: "14px",
    border: "1px solid rgba(148,163,184,0.14)",
    background: "rgba(12,12,24,0.88)",
    padding: "12px",
  },
};

function toneColor(tone: "good" | "bad" | "warn" | "neutral") {
  if (tone === "good") return { color: "#22c55e", background: "rgba(34,197,94,0.12)" };
  if (tone === "bad") return { color: "#ef4444", background: "rgba(239,68,68,0.12)" };
  if (tone === "warn") return { color: "#f59e0b", background: "rgba(245,158,11,0.12)" };
  return { color: "#94a3b8", background: "rgba(148,163,184,0.10)" };
}

function Pill({ children, tone = "neutral", title }: { children: ReactNode; tone?: "good" | "bad" | "warn" | "neutral"; title?: string }) {
  return <span title={title} style={{ ...S.pill, ...toneColor(tone) }}>{children}</span>;
}

function verdictLabel(row: CommandCenterRowV2): { label: string; tone: "good" | "bad" | "warn" | "neutral" } {
  switch (row.coachFlow.verdict) {
    case "confirm_long": return { label: "Confirm Long", tone: "good" };
    case "confirm_short": return { label: "Confirm Short", tone: "good" };
    case "conflict": return { label: "Conflict", tone: "bad" };
    case "protect_exit": return { label: "Protect / Exit Risk", tone: "bad" };
    case "blocked": return { label: "Blocked", tone: "bad" };
    case "wait": return { label: "Wait", tone: "warn" };
  }
}

function strategyTone(strategy: CommandCenterStrategyState): "good" | "bad" | "warn" | "neutral" {
  if (strategy.state === "open_trade") return "warn";
  if (strategy.direction === "long") return "good";
  if (strategy.direction === "short") return "bad";
  return "neutral";
}

function strategyLabel(strategy: CommandCenterStrategyState): string {
  if (strategy.state === "no_signal") return "No signal";
  const prefix = strategy.state === "open_trade" ? "OPEN" : strategy.agent?.toUpperCase() ?? "SIGNAL";
  return `${prefix} ${strategy.direction?.toUpperCase() ?? "?"}`;
}

function formatMaybePrice(value: number | null, symbol: string): string {
  return typeof value === "number" ? formatPrice(value, symbol) : "—";
}

function formatAge(ageSec: number | null): string {
  if (ageSec === null) return "age n/a";
  if (ageSec < 60) return `${Math.round(ageSec)}s`;
  return `${Math.round(ageSec / 60)}m`;
}

function formatDecimal(value: number | null, digits: number): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  return value.toFixed(digits);
}

function orderFlowRead(row: CommandCenterRowV2): { headline: string; detail: string; tone: "good" | "bad" | "warn" | "neutral" } {
  const status = row.proof.orderFlow.status ?? "missing";
  if (status !== "fresh") {
    return {
      headline: status === "stale" ? "Order flow stale" : "Order flow unavailable",
      detail: `Do not use for entry · ${formatAge(row.proof.orderFlow.ageSec)}`,
      tone: "warn",
    };
  }

  const direction = (row.proof.orderFlow.institutionalDirection ?? "neutral").toLowerCase();
  const score = row.proof.orderFlow.institutionalScore;
  const pressure = typeof score === "number" ? `pressure ${score.toFixed(2)}` : "pressure n/a";
  const defense = row.proof.orderFlow.absorptionBid
    ? "bid defended"
    : row.proof.orderFlow.absorptionAsk
      ? "offer defended"
      : row.proof.orderFlow.icebergBid
        ? "hidden bid"
        : row.proof.orderFlow.icebergAsk
          ? "hidden offer"
          : "no defended level";

  if (direction.includes("buy")) return { headline: "Buyers pressing", detail: `${pressure} · ${defense}`, tone: "good" };
  if (direction.includes("sell")) return { headline: "Sellers pressing", detail: `${pressure} · ${defense}`, tone: "bad" };
  return { headline: "No clear pressure", detail: `${pressure} · ${defense}`, tone: "neutral" };
}

function volumeFlowRead(row: CommandCenterRowV2): { headline: string; detail: string; tone: "good" | "bad" | "warn" | "neutral" } {
  const bias = (row.coachFlow.volumeFlowBias ?? "").toLowerCase();
  const cmf = formatDecimal(row.proof.volumeFlow.cmf20, 2);
  const mfi = formatDecimal(row.proof.volumeFlow.mfi14, 1);
  const detail = `CMF ${cmf} · MFI ${mfi}`;

  if (!bias || bias.includes("n/a") || bias.includes("missing")) {
    return { headline: "Volume unavailable", detail: "Wait for live tape confirmation", tone: "warn" };
  }
  if (bias.includes("bull") || bias.includes("buy") || bias.includes("long")) return { headline: "Volume supports longs", detail, tone: "good" };
  if (bias.includes("bear") || bias.includes("sell") || bias.includes("short")) return { headline: "Volume supports shorts", detail, tone: "bad" };
  if (bias.includes("neutral")) return { headline: "Volume neutral", detail, tone: "neutral" };
  return { headline: bias, detail, tone: "neutral" };
}

function biasTone(bias?: string | null): "good" | "bad" | "warn" | "neutral" {
  const normalized = (bias ?? "").toLowerCase();
  if (normalized.includes("bull")) return "good";
  if (normalized.includes("bear")) return "bad";
  if (normalized.includes("mixed")) return "warn";
  return "neutral";
}

function stateTone(state?: string | null): "good" | "bad" | "warn" | "neutral" {
  const normalized = (state ?? "").toLowerCase();
  if (normalized.includes("markup") || normalized.includes("accumulation")) return "good";
  if (normalized.includes("markdown") || normalized.includes("distribution")) return "bad";
  if (normalized.includes("rotation")) return "warn";
  return "neutral";
}

function compactBias(bias?: string | null): string {
  if (!bias) return "n/a";
  return bias.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function stackTone(verdict?: string | null): "good" | "bad" | "warn" | "neutral" {
  const normalized = (verdict ?? "").toUpperCase();
  if (normalized.includes("LONG_CONFIRMATION")) return "good";
  if (normalized.includes("SHORT_CONFIRMATION")) return "bad";
  if (normalized.includes("DEVELOPING") || normalized.includes("CONFLICT")) return "warn";
  return "neutral";
}

function sideTone(side?: string | null): "good" | "bad" | "warn" | "neutral" {
  const normalized = (side ?? "").toLowerCase();
  if (normalized === "long") return "good";
  if (normalized === "short") return "bad";
  if (normalized === "neutral") return "neutral";
  return "warn";
}

function stackLabel(verdict?: string | null): string {
  if (!verdict) return "No Stack";
  return compactBias(verdict.toLowerCase());
}

function MarketFlowStackRead({ row }: { row: CommandCenterRowV2 }) {
  const stack = row.marketFlowStack;
  if (!stack) return <div style={S.muted}>Stack unavailable</div>;
  const riskFlags = Array.isArray(stack.risk_flags) ? stack.risk_flags : [];
  const score = typeof stack.alignment_score === "number" ? stack.alignment_score : null;
  return (
    <div style={{ display: "grid", gap: 6, minWidth: 190 }}>
      <Pill tone={stackTone(stack.verdict)} title="Combined MBP-1 order book plus Databento trade-tape confirmation">
        {stackLabel(stack.verdict)}
      </Pill>
      <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
        <Pill tone={sideTone(stack.book_side)} title={stack.book_reason}>Book {compactBias(stack.book_side)}</Pill>
        <Pill tone={sideTone(stack.tape_5m)}>5m {compactBias(stack.tape_5m)}</Pill>
        <Pill tone={sideTone(stack.tape_15m)}>15m {compactBias(stack.tape_15m)}</Pill>
        <Pill tone={sideTone(stack.tape_30m)}>30m {compactBias(stack.tape_30m)}</Pill>
      </div>
      <div style={S.muted}>alignment {score ?? "—"} · {riskFlags.length ? riskFlags.slice(0, 2).join(" · ") : "no stack risk"}</div>
      <div style={S.muted}>{stack.human_summary ?? "Derived from order book + tape"}</div>
    </div>
  );
}

function AccumulationRead({ row }: { row: CommandCenterRowV2 }) {
  const windows = row.proof.tapeAccumulation;
  if (!windows) return <div style={S.muted}>Accumulation tape unavailable</div>;
  return (
    <div style={{ display: "grid", gap: 7, minWidth: 190 }}>
      {["5m", "15m", "30m"].map((key) => {
        const item = windows[key];
        if (!item) {
          return <div key={key} style={S.muted}>{key}: n/a</div>;
        }
        const tapeBias = item.tape_bias ?? "neutral";
        const flowState = item.flow_state ?? "ROTATION";
        const deltaRatio = typeof item.delta_ratio === "number" ? `${Math.round(item.delta_ratio * 100)}%` : "—";
        return (
          <div key={key} style={{ display: "grid", gap: 3 }}>
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ ...S.strong, minWidth: 28 }}>{key}</span>
              <Pill tone={biasTone(tapeBias)} title="Aggressive trade-tape delta, not order-book depth">Tape {compactBias(tapeBias)}</Pill>
              <Pill tone={stateTone(flowState)} title="Delta plus price-response sequence state">{compactBias(flowState)}</Pill>
            </div>
            <div style={S.muted}>delta {deltaRatio} · {item.trade_count ?? 0} trades</div>
          </div>
        );
      })}
      <div style={S.muted}>source {row.proof.tapeAccumulationSource ?? "live tape"}</div>
    </div>
  );
}

function signalChip(strategy: CommandCenterStrategyState) {
  const label = strategy.agent?.toUpperCase() ?? "—";
  if (strategy.state === "no_signal") return <Pill>{label}: none</Pill>;
  return <Pill tone={strategyTone(strategy)}>{label}: {strategy.direction?.toUpperCase()}</Pill>;
}

function rowBorder(row: CommandCenterRowV2): string {
  if (row.coachFlow.verdict === "blocked" || row.coachFlow.verdict === "conflict" || row.coachFlow.verdict === "protect_exit") return "rgba(239,68,68,0.5)";
  if (row.coachFlow.verdict === "confirm_long" || row.coachFlow.verdict === "confirm_short") return "rgba(34,197,94,0.5)";
  return "rgba(148,163,184,0.10)";
}

function FocusPane({ row }: { row: CommandCenterRowV2 }) {
  return (
    <section style={S.detail} aria-label="Focused symbol coach read">
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={S.title}>{row.symbol} Coach Read</div>
          <div style={S.subtitle}>{row.coachFlow.entryAction ?? "No entry action"} · {row.coachFlow.entryReason ?? "No reason supplied"}</div>
        </div>
        <Pill tone={verdictLabel(row).tone}>{verdictLabel(row).label}</Pill>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 10, marginTop: 12 }}>
        <div>
          <div style={S.muted}>Trigger</div>
          <div style={S.strong}>{row.coachFlow.trigger ?? "Wait for coach trigger"}</div>
        </div>
        <div>
          <div style={S.muted}>Invalidation</div>
          <div style={S.strong}>{row.coachFlow.invalidation ?? "Not available"}</div>
        </div>
        <div>
          <div style={S.muted}>Delta 3/5/10/15m</div>
          <div style={S.strong}>{["3m", "5m", "10m", "15m"].map((key) => row.proof.delta[key] ?? "—").join(" / ")}</div>
        </div>
        <div>
          <div style={S.muted}>Warnings</div>
          <div style={S.strong}>{[...row.coachFlow.blockers, ...row.proof.dataWarnings].slice(0, 4).join(" · ") || "None"}</div>
        </div>
      </div>
    </section>
  );
}

export function CommandCenterV2({ symbols, session, intelMap, signals, openTrades, loading, error, transportLabel }: CommandCenterV2Props) {
  const rows = useMemo(() => buildCommandCenterRowsV2({ symbols, session, intelMap, signals, openTrades }), [symbols, session, intelMap, signals, openTrades]);
  const [selectedSymbol, setSelectedSymbol] = useState<string>(symbols[0] ?? "ES");
  const selected = rows.find((row) => row.symbol === selectedSymbol) ?? rows[0];
  const liveCount = rows.filter((row) => row.price.status === "fresh").length;
  const blockedCount = rows.filter((row) => row.coachFlow.verdict === "blocked").length;

  if (loading) return <section style={S.detail}>Loading Command Center V2…</section>;
  if (error) return <section style={S.detail}>Command Center V2 unavailable: {error.message}</section>;

  return (
    <main style={S.shell} aria-label="Command Center V2 trading cockpit">
      <section style={S.header}>
        <div>
          <div style={S.title}>Command Center V2</div>
          <div style={S.subtitle}>Signal × live coach-flow cockpit · ORB/FBD/MR + order flow + volume flow</div>
        </div>
        <div style={S.health}>
          <Pill tone={transportLabel === "LIVE WS" ? "good" : "warn"}>{transportLabel}</Pill>
          <Pill tone={liveCount === symbols.length ? "good" : "warn"}>{liveCount}/{symbols.length} fresh prices</Pill>
          <Pill tone={blockedCount > 0 ? "bad" : "good"}>{blockedCount} blocked</Pill>
        </div>
      </section>

      <section style={S.grid}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Symbol</th>
              <th style={S.th}>ORB / FBD / MR</th>
              <th style={S.th}>Primary</th>
              <th style={S.th}>Coach Verdict</th>
              <th style={S.th}>Order Book Now</th>
              <th style={S.th}>Volume Flow</th>
              <th style={S.th}>Tape / Volume Accumulation</th>
              <th style={S.th}>Market Flow Stack</th>
              <th style={S.th}>Action Proof</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const verdict = verdictLabel(row);
              const orderFlow = orderFlowRead(row);
              const volumeFlow = volumeFlowRead(row);
              return (
                <tr key={row.symbol} onClick={() => setSelectedSymbol(row.symbol)} style={{ cursor: "pointer", borderLeft: `3px solid ${rowBorder(row)}`, background: selected?.symbol === row.symbol ? "rgba(59,130,246,0.08)" : undefined }}>
                  <td style={S.td}>
                    <div style={S.sym}>{row.symbol}</div>
                    <div style={S.muted}>{formatMaybePrice(row.price.value, row.symbol)} · {row.price.status} · {formatAge(row.price.ageSec)}</div>
                  </td>
                  <td style={S.td}>
                    <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>{row.strategySignals.map((strategy, index) => <span key={`${row.symbol}-${index}`}>{signalChip(strategy)}</span>)}</div>
                  </td>
                  <td style={S.td}>
                    <Pill tone={strategyTone(row.strategy)}>{strategyLabel(row.strategy)}</Pill>
                    <div style={{ ...S.muted, marginTop: 5 }}>E {formatMaybePrice(row.strategy.entry, row.symbol)} · SL {formatMaybePrice(row.strategy.stopLoss, row.symbol)} · TP1 {formatMaybePrice(row.strategy.tp1, row.symbol)}</div>
                    <div style={S.muted}>R:R {row.strategy.rrTp1 ?? "—"}</div>
                  </td>
                  <td style={S.td}>
                    <Pill tone={verdict.tone}>{verdict.label}</Pill>
                    <div style={{ ...S.muted, marginTop: 5 }}>{row.coachFlow.flowState ?? "flow state n/a"} · {row.coachFlow.flowBias ?? "bias n/a"}</div>
                    <div style={S.muted}>{row.coachFlow.entryAction ?? "wait"}</div>
                  </td>
                  <td style={S.td}>
                    <Pill tone={orderFlow.tone}>{orderFlow.headline}</Pill>
                    <div style={{ ...S.muted, marginTop: 5 }}>{orderFlow.detail}</div>
                    <div style={S.muted}>{row.proof.orderFlow.status ?? "missing"} · {formatAge(row.proof.orderFlow.ageSec)}</div>
                  </td>
                  <td style={S.td}>
                    <Pill tone={volumeFlow.tone}>{volumeFlow.headline}</Pill>
                    <div style={{ ...S.muted, marginTop: 5 }}>{volumeFlow.detail}</div>
                    <div style={S.muted}>Setup scores: FBD {formatDecimal(row.proof.volumeFlow.fbdScore, 2)} · MR {formatDecimal(row.proof.volumeFlow.mrScore, 2)}</div>
                  </td>
                  <td style={S.td}><AccumulationRead row={row} /></td>
                  <td style={S.td}><MarketFlowStackRead row={row} /></td>
                  <td style={S.td}>
                    <div style={S.strong}>{row.coachFlow.entryReason ?? row.coachFlow.trigger ?? "No trigger yet"}</div>
                    <div style={S.muted}>{row.coachFlow.blockers.slice(0, 2).join(" · ") || "No hard blocker"}</div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      {selected && <FocusPane row={selected} />}
    </main>
  );
}
